import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Ticket, Bus, MapPin, User, Phone, QrCode, 
  Printer, ArrowLeft, AlertCircle, Calendar, Clock,
  DollarSign, Package, Wifi, Wind, Usb, Bath, Users,
  FileText, Shield, Baby, CreditCard, MessageCircle, XCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { formatTicketEscPos, printBluetoothEscPos } from '../lib/bluetoothPrinter';
import { useAuth } from '../context/AuthContext';
import { getSystemDetailsUrl } from '../lib/qrCode';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TRAVEL_REQUIREMENTS = [
  { icon: '🛂', text: 'Bring valid Passport for border crossing' },
  { icon: '👶', text: 'Children under 18: Parent consent affidavit required' },
  { icon: '📄', text: "Children under 18: Original child's birth certificate required" },
  { icon: '🎫', text: 'Present this ticket at boarding gate' },
  { icon: '⏰', text: 'Arrive at station 30 minutes before departure' }
];

const TicketPage = () => {
  const { bookingRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const printRef = useRef(null);

  const [booking, setBooking] = useState(null);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [btPrinting, setBtPrinting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBooking();
  }, [bookingRef]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const bookingRes = await axios.get(`${API_URL}/bookings/${bookingRef}`);
      setBooking(bookingRes.data);

      // Fetch trip details for amenities
      if (bookingRes.data.trip_id) {
        const tripRes = await axios.get(`${API_URL}/trips/${bookingRes.data.trip_id}`);
        setTrip(tripRes.data);
      }
    } catch (err) {
      setError('Failed to load ticket');
      toast.error('Could not load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const recordPrint = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API_URL}/print-history/record`,
        { document_type: 'ticket', reference: bookingRef },
        { headers }
      );
    } catch (e) {
      console.warn('Failed to record print history:', e);
    }
  };

  const canUserPrint = () => {
    if (!user) return true; // public passengers can print their own ticket
    if (user.role === 'conductor') {
      const assigned = trip?.conductor_id === user.id || booking?.conductor_id === user.id;
      if (!assigned) {
        toast.error('You are not assigned as host/conductor to this trip and cannot print tickets for it.');
        return false;
      }
    } else if (user.role === 'driver') {
      const assigned = trip?.driver_id === user.id || booking?.driver_id === user.id;
      if (!assigned) {
        toast.error('You are not assigned as driver to this trip and cannot print tickets for it.');
        return false;
      }
    }
    return true;
  };

  const handleBluetoothPrint = async () => {
    if (!booking) {
      toast.error('Ticket data is loading.');
      return;
    }
    if (!canUserPrint()) return;
    const isZeroFare = (booking.total_amount || 0) <= 0 || booking.is_complimentary;
    const paymentPending =
      booking.is_online_booking && !isZeroFare && (
        booking.payment_status === 'pending' ||
        (booking.status === 'pending' && booking.payment_method === 'cash' && Number(booking.paid_amount || 0) <= 0)
      );
    if (paymentPending) {
      toast.error('Cannot print ticket: cash payment has not been approved yet.');
      return;
    }

    setBtPrinting(true);
    try {
      const bytes = formatTicketEscPos(booking, trip);
      await printBluetoothEscPos(bytes);
      await recordPrint();
      toast.success('Ticket sent to Bluetooth Thermal Printer & print logged!');
    } catch (err) {
      toast.error(err.message || 'Bluetooth printing failed.');
    } finally {
      setBtPrinting(false);
    }
  };

  const handlePrint = () => {
    if (!booking) {
      toast.error('Ticket data is still loading.');
      return;
    }
    if (!canUserPrint()) return;
    // Block printing if cash payment has NOT been approved yet (exempt zero-fare)
    const isZeroFare = (booking.total_amount || 0) <= 0 || booking.is_complimentary;
    const paymentPending =
      booking.is_online_booking && !isZeroFare && (
        booking.payment_status === 'pending' ||
        (booking.status === 'pending' && booking.payment_method === 'cash' && Number(booking.paid_amount || 0) <= 0)
      );
    if (paymentPending) {
      toast.error(
        'Cannot print ticket: cash payment has not been approved yet. Please ask station staff to approve the payment first.',
        { duration: 5000 }
      );
      return;
    }
    recordPrint();
    window.print();
  };

  const handleWhatsAppShare = () => {
    if (!booking) return;
    const ticketUrl = `${window.location.origin}/ticket/${booking.booking_ref}`;
    const passenger = booking.passengers?.[0] || {};
    const depTime = booking.departure_datetime || booking.trip_details?.departure || booking.trip_details?.departure_datetime || trip?.departure_datetime || '';
    const dep = depTime ? depTime.slice(0, 16).replace('T', ' ') : 'N/A';
    const message = encodeURIComponent(
      `🚌 *PANASHE COACHES — Your Ticket*\n` +
      `Ref: *${booking.booking_ref}*\n` +
      `Route: ${booking.origin || booking.trip_details?.origin || ''} → ${booking.destination || booking.trip_details?.destination || ''}\n` +
      `Departure: ${dep}\n` +
      `Passenger: ${passenger.full_name || ''}\n` +
      `Seat(s): ${booking.seat_numbers?.join(', ') || 'N/A'}\n` +
      `Status: ${(booking.status || '').toUpperCase()}\n\n` +
      `📱 View ticket: ${ticketUrl}\n\n` +
      `Show this ticket to the host when boarding.`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleCancelBooking = async () => {
    if (!booking || !window.confirm('Cancel this ticket? This will release the reserved seat.')) return;

    setCancelling(true);
    try {
      await axios.post(`${API_URL}/bookings/${booking.booking_ref}/cancel`, null, {
        params: { reason: 'Cancelled by passenger online' }
      });
      setBooking((currentBooking) => ({
        ...currentBooking,
        status: 'cancelled',
        cancellation_reason: 'Cancelled by passenger online'
      }));
      toast.success('Your ticket has been cancelled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel this ticket.');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getAmenityIcon = (amenity) => {
    const icons = {
      'WiFi': <Wifi className="h-3 w-3" />,
      'AC': <Wind className="h-3 w-3" />,
      'USB Charging': <Usb className="h-3 w-3" />,
      'Toilet': <Bath className="h-3 w-3" />
    };
    return icons[amenity] || null;
  };

  const currencySymbol = booking?.currency === 'ZAR' ? 'R' : '$';

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !booking) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Ticket Not Found</h2>
          <p className="text-slate-600 mb-4">Reference: {bookingRef}</p>
          <Button onClick={() => navigate(-1)} className="btn-panashe">
            Go Back
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const passenger = booking.passengers?.[0] || {};
  const departureDate = booking.departure_datetime || booking.trip_details?.departure || booking.trip_details?.departure_datetime || trip?.departure_datetime;
  
  const formatDeparture = (dt) => {
    if (!dt) return 'N/A';
    try {
      const clean = typeof dt === 'string' ? dt.trim().replace(' ', 'T') : dt;
      const parsed = parseISO(clean);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'EEE, dd MMM yyyy @ HH:mm');
      }
      const d = new Date(dt);
      if (!isNaN(d.getTime())) {
        return format(d, 'EEE, dd MMM yyyy @ HH:mm');
      }
      return String(dt);
    } catch {
      return String(dt);
    }
  };

  const busAmenities = booking.bus_amenities || trip?.amenities || [];
  const isZeroFare = (booking.total_amount || 0) <= 0 || booking.is_complimentary;
  const isPaymentPending =
    booking.is_online_booking && !isZeroFare && (
      booking.payment_status === 'pending' ||
      (booking.status === 'pending' && booking.payment_method === 'cash' && Number(booking.paid_amount || 0) <= 0)
    );

  // Pay Forward is ONLY true when explicitly enabled with deferred balance (pay_forward_amount > 0)
  const isPayForward =
    Boolean(booking?.is_pay_forward) &&
    Number(booking?.pay_forward_amount || 0) > 0;
  const displayStatus = booking.is_online_booking === false && booking.status === 'pending'
    ? 'confirmed'
    : booking.status;
  const canPassengerCancel =
    !location.pathname.startsWith('/qr/') &&
    Boolean(user?.id && booking.user_id === user.id && user.role === 'passenger') &&
    !['cancelled', 'refunded', 'completed'].includes(booking.status);
  const isPassengerSelfBooking =
    booking.is_online_booking === true &&
    Boolean(user?.id && booking.user_id === user.id && user.role === 'passenger');

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Action Buttons - Hidden on Print */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
          {!isPassengerSelfBooking && (
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}

          <div className="flex items-center gap-2">
            {canPassengerCancel && (
              <Button
                onClick={handleCancelBooking}
                disabled={cancelling}
                variant="outline"
                className="border-red-500 text-red-700 hover:bg-red-50 font-semibold"
                title="Cancel this ticket"
                data-testid="cancel-ticket-btn"
              >
                <XCircle className="h-4 w-4 mr-1.5" />
                {cancelling ? 'Cancelling...' : 'Cancel Ticket'}
              </Button>
            )}
            {!isPassengerSelfBooking && <Button
              onClick={handleBluetoothPrint}
              disabled={btPrinting || isPaymentPending}
              variant="outline"
              className="border-indigo-500 text-indigo-700 hover:bg-indigo-50 font-semibold"
              title="Connect & print directly to Bluetooth Thermal Printer (58mm/80mm)"
              data-testid="bt-print-btn"
            >
              <Wifi className="h-4 w-4 mr-1.5 text-indigo-600" />
              {btPrinting ? 'Printing...' : '🛜 Bluetooth Thermal Print'}
            </Button>}

            <Button
              onClick={handleWhatsAppShare}
              variant="outline"
              className="border-green-500 text-green-700 hover:bg-green-50 font-semibold"
              title="Share ticket via WhatsApp"
              data-testid="whatsapp-share-btn"
            >
              <MessageCircle className="h-4 w-4 mr-1.5 text-green-600" />
              Share on WhatsApp
            </Button>

            <Button
              onClick={handlePrint}
              className={isPaymentPending ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'btn-panashe'}
              title={isPaymentPending ? 'Cash payment not yet approved — printing blocked' : 'Print this ticket'}
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              {isPaymentPending ? '🔒 Print Blocked (Pending Payment)' : 'Standard Print'}
            </Button>
          </div>
        </div>

        {/* Printable Ticket */}
        <div ref={printRef} className="ticket-container">
          <Card className="card-panashe overflow-hidden" data-testid="ticket-card">
            {/* Header: 3-column — Logo | Company+Ref | QR Code */}
            <div className="bg-gradient-to-r from-panashe-blue to-red-900 p-5 text-white">
              <div className="header-row flex items-center justify-between gap-3">
                {/* Left: Logo */}
                <div className="logo-box bg-white p-1.5 border-2 border-white/30 rounded-lg flex-shrink-0 shadow-sm">
                  <img 
                    src="/panashe-logo.jpg" 
                    alt="Panashe Coaches" 
                    className="logo rounded object-cover"
                    style={{ width: 80, height: 80, display: 'block' }}
                  />
                </div>

                {/* Center: Company name + booking ref + status */}
                <div className="header-center flex-1 text-center">
                  <h1 className="company-name font-heading text-2xl font-bold tracking-wide">PANASHE COACHES</h1>
                  <p className="ticket-type text-white/80 text-sm mt-0.5">Passenger Ticket</p>
                  <p className="booking-ref font-heading text-2xl font-bold mt-1 font-mono tracking-wider">{booking.booking_ref}</p>
                  <Badge className={`${getStatusColor(displayStatus)} mt-1.5`}>
                    {displayStatus?.toUpperCase()}
                  </Badge>
                </div>

                {/* Right: QR Code */}
                <div className="flex-shrink-0 text-center">
                  <div className="qr-box bg-white p-1.5 border-2 border-white/30 rounded-lg shadow-sm">
                    <QRCodeSVG 
                      value={getSystemDetailsUrl(booking.booking_ref, 'ticket')} 
                      size={144}
                      level="H"
                      includeMargin
                    />
                  </div>
                  <p className="scan-label text-xs text-white/70 mt-1">Scan to verify</p>
                </div>
              </div>
            </div>

            {/* PENDING CASH PAYMENT BANNER */}
            {booking.is_online_booking && booking.status === 'pending' && (booking.payment_status === 'pending' || booking.payment_status === 'partial') && (
              <div className="bg-amber-50 border-l-4 border-amber-500 px-5 py-3 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-amber-800 text-sm flex items-center gap-2">
                    ⏳ AWAITING CASH PAYMENT APPROVAL
                    {isPayForward && (
                      <span className="bg-amber-200 text-amber-900 text-xs px-2 py-0.5 rounded font-mono font-semibold">PAY FORWARD</span>
                    )}
                  </p>
                  <p className="text-amber-700 text-xs mt-0.5">
                    This ticket is <strong>PENDING</strong>. {isPayForward ? 'This is a Pay Forward booking. ' : ''}The passenger must pay cash at the station before boarding. Station staff must approve payment to confirm this ticket.
                  </p>
                  <p className="text-amber-700 text-xs mt-1">
                    💰 Balance due at station: <strong>
                      {isPayForward && booking.pay_forward_currency && booking.pay_forward_currency !== booking.currency ? (
                        <>
                          {booking.currency} {Number(booking.pay_forward_balance || (booking.total_amount - (booking.paid_amount || 0))).toFixed(2)}
                          {' ('}
                          {booking.pay_forward_currency === 'ZAR' ? 'R' : '$'}
                          {Number(booking.pay_forward_balance_alt || (booking.pay_forward_currency === 'ZAR' ? ((booking.pay_forward_balance || 0) * (booking.fx_rate || 18.5)) : ((booking.pay_forward_balance || 0) / (booking.fx_rate || 18.5)))).toFixed(2)}
                          {` ${booking.pay_forward_currency} Pay Forward)`}
                        </>
                      ) : (
                        `${booking.currency} ${Number(booking.total_amount - (booking.paid_amount || 0)).toFixed(2)}`
                      )}
                    </strong>
                  </p>
                </div>
              </div>
            )}

            <CardContent className="p-4 space-y-3">

              {/* ── ROW 1: Departure bar (full width) ── */}
              <div className="flex items-center justify-between bg-panashe-red/10 px-4 py-2.5 rounded-lg text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Seat(s)</p>
                  <p className="font-bold text-lg text-panashe-red leading-tight">{booking.seat_numbers?.join(', ')}</p>
                </div>
                <div className="text-center flex-1 px-4">
                  <p className="text-slate-500 text-xs">Departure Date &amp; Time</p>
                  <p className="font-bold text-panashe-red text-base">{formatDeparture(departureDate)}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs">Issued</p>
                  <p className="font-medium text-xs">{booking.created_at ? format(parseISO(booking.created_at), 'dd MMM yyyy') : 'N/A'}</p>
                </div>
              </div>

              {/* ── ROW 2: Trip Details + Passenger Details side by side ── */}
              <div className="grid grid-cols-2 gap-3 text-sm">

                {/* LEFT: Trip Details */}
                <div className="border rounded-lg p-3">
                  <h3 className="font-heading font-bold text-slate-900 mb-2 flex items-center gap-1.5 text-sm border-b pb-1.5">
                    <Bus className="h-4 w-4 flex-shrink-0" />
                    Trip Details
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 text-xs flex-shrink-0">Origin</span>
                      <span className="font-bold text-blue-700 text-right">{booking.origin || trip?.origin || booking.trip_details?.origin || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 text-xs flex-shrink-0">Destination</span>
                      <span className="font-bold text-green-700 text-right">{booking.destination || trip?.destination || booking.trip_details?.destination || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 text-xs flex-shrink-0">Departure</span>
                      <span className="font-bold text-slate-800 text-right text-xs">{formatDeparture(departureDate)}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 text-xs flex-shrink-0">Bus Reg</span>
                      <span className="font-medium font-mono text-right text-xs">{booking.bus_registration || trip?.bus_registration || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 text-xs flex-shrink-0">Route</span>
                      <span className="font-medium text-right text-xs">{booking.route_name || trip?.route_name || 'N/A'}</span>
                    </div>
                  </div>
                  {/* Bus Amenities */}
                  {busAmenities.length > 0 && (
                    <div className="mt-2 pt-1.5 border-t">
                      <p className="text-slate-500 text-xs mb-1">Amenities:</p>
                      <div className="flex flex-wrap gap-1">
                        {busAmenities.map((amenity, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px] flex items-center gap-0.5 py-0 px-1.5">
                            {getAmenityIcon(amenity)} {amenity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Trip Crew: Driver & Host */}
                  <div className="mt-2 pt-1.5 border-t">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1 font-semibold text-slate-700">
                      <Users className="h-3 w-3 text-panashe-blue" /> Trip Crew
                    </p>
                    <div className="space-y-1">
                      <p className="text-xs">
                        <span className="text-slate-500">Driver:</span>{' '}
                        <span className="font-semibold text-slate-800">
                          {booking.driver_name || booking.trip_details?.driver_name || trip?.driver_name || 'Takudzwa Kuwenyi (Driver)'}
                        </span>
                      </p>
                      <p className="text-xs">
                        <span className="text-slate-500">Host:</span>{' '}
                        <span className="font-semibold text-emerald-800">
                          {booking.conductor_name || booking.trip_details?.conductor_name || trip?.conductor_name || 'Chipo Chiduku (Host)'}
                        </span>
                      </p>
                      {(booking.conductor_phone || booking.trip_details?.conductor_phone || trip?.conductor_phone) && (
                        <p className="text-xs">
                          <span className="text-slate-500">Host Phone:</span>{' '}
                          <span className="font-mono text-slate-700">
                            {booking.conductor_phone || booking.trip_details?.conductor_phone || trip?.conductor_phone || '+263 786 563 430'}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: Passenger Details */}
                <div className="bg-blue-50 rounded-lg p-3">
                  <h3 className="font-heading font-bold text-blue-900 mb-2 flex items-center gap-1.5 text-sm border-b border-blue-200 pb-1.5">
                    <User className="h-4 w-4 flex-shrink-0" />
                    Passenger Details
                  </h3>
                  <div className="space-y-1.5">
                    <div>
                      <p className="text-slate-500 text-xs">Full Name</p>
                      <p className="font-bold">{passenger.full_name || 'N/A'}</p>
                    </div>
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="text-slate-500 text-xs">Passport Number</p>
                        <p className="font-medium text-xs font-mono">{passenger.passport_number || (passenger.is_local ? 'Local (Exempt)' : 'N/A')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500 text-xs">Nationality</p>
                        <p className="font-medium text-xs">{passenger.nationality || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="text-slate-500 text-xs">Phone</p>
                        <p className="font-medium text-xs">{passenger.phone || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500 text-xs">Sex / Age</p>
                        <p className="font-medium text-xs">{passenger.sex || 'N/A'} {passenger.age ? `(${passenger.age} yrs)` : ''}</p>
                      </div>
                    </div>

                    {/* Boarding and Drop-off Points */}
                    {(passenger.boarding_point || passenger.dropoff_point || booking.pickup_point || booking.dropoff_point) && (
                      <div className="flex justify-between gap-2 pt-1 border-t border-blue-200">
                        <div>
                          <p className="text-slate-500 text-xs">Boarding Point</p>
                          <p className="font-medium text-xs text-green-700">{passenger.boarding_point || booking.pickup_point || booking.origin}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-500 text-xs">Drop-off Point</p>
                          <p className="font-medium text-xs text-red-700">{passenger.dropoff_point || booking.dropoff_point || booking.destination}</p>
                        </div>
                      </div>
                    )}

                    {passenger.luggage_refs?.length > 0 && (
                      <div className="pt-1 border-t border-blue-200">
                        <p className="text-slate-500 text-xs flex items-center gap-1">
                          <Package className="h-3 w-3" /> Luggage travelling with passenger
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {passenger.luggage_refs.map((ref) => (
                            <button
                              key={ref}
                              type="button"
                              onClick={() => navigate(`/luggage-ticket/${encodeURIComponent(ref)}`)}
                              className="font-mono text-xs font-bold text-purple-700 underline"
                            >
                              {ref}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Category Badges */}
                    <div className="flex flex-wrap gap-1 pt-1">
                      {passenger.is_child_under_5 && (
                        <span className="text-[10px] bg-blue-100 text-blue-800 font-semibold px-1.5 py-0.5 rounded">👶 Child &lt; 5 (Free)</span>
                      )}
                      {passenger.is_complementary && (
                        <span className="text-[10px] bg-purple-100 text-purple-800 font-semibold px-1.5 py-0.5 rounded">🎟️ Staff Comp (Free)</span>
                      )}
                      {passenger.is_local && (
                        <span className="text-[10px] bg-green-100 text-green-800 font-semibold px-1.5 py-0.5 rounded">🏠 Local Passenger</span>
                      )}
                      {passenger.is_border_jumper && (
                        <span className="text-[10px] bg-orange-100 text-orange-800 font-semibold px-1.5 py-0.5 rounded">💰 Additional Charges</span>
                      )}
                      {passenger.education && (
                        <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">🎓 {passenger.education}</span>
                      )}
                      {passenger.occupation && (
                        <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">💼 {passenger.occupation}</span>
                      )}
                    </div>

                    {/* Paid From Location */}
                    {booking.payment_location && (
                      <div className="text-[11px] bg-white p-1.5 rounded border border-blue-200 mt-1">
                        <p><span className="text-slate-500">Paid From:</span> <span className="font-medium">{booking.payment_location}</span></p>
                      </div>
                    )}

                    {/* Next of Kin Details */}
                    <div className="flex justify-between gap-2 pt-1 border-t border-blue-200">
                      <div>
                        <p className="text-slate-500 text-xs">Next of Kin</p>
                        <p className="font-medium text-xs">{passenger.next_of_kin_name || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500 text-xs">NOK Phone</p>
                        <p className="font-medium text-xs">{passenger.next_of_kin_phone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── ROW 3: Payment + Requirements side by side ── */}
              <div className="grid grid-cols-2 gap-3 text-sm">

                {/* LEFT: Payment Details */}
                <div className="border rounded-lg p-3">
                  <h3 className="font-heading font-bold text-slate-900 mb-2 flex items-center gap-1.5 text-sm border-b pb-1.5">
                    <CreditCard className="h-4 w-4 flex-shrink-0" />
                    Payment Details
                  </h3>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <p className="text-slate-500">Currency</p>
                      <p className="font-bold">{booking.currency || 'USD'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Price/Seat</p>
                      <p className="font-medium">{currencySymbol}{Number(booking.negotiated_price_per_seat ?? (booking.total_amount / (booking.seat_numbers?.length || 1))).toFixed(2)}</p>
                      {booking.negotiated_price_applied && (
                        <p className="text-[10px] text-amber-700 font-semibold">Negotiated fare</p>
                      )}
                    </div>
                    <div>
                      <p className="text-slate-500">Seats</p>
                      <p className="font-medium">{booking.seat_numbers?.length || 1}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Total Fare</p>
                      <p className="font-bold text-base">{currencySymbol}{booking.total_amount?.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="mt-2 pt-1.5 border-t space-y-1.5">
                    {/* Amount Paid at Booking */}
                    <div className="bg-green-50 p-2 rounded flex justify-between items-center">
                      <p className="text-green-700 text-xs">Amount Paid</p>
                      <p className="font-bold text-green-700">
                        {currencySymbol}{(booking.paid_amount || 0).toFixed(2)}
                      </p>
                    </div>

                    {/* Balance Due (Station or Destination) */}
                    {(booking.total_amount - (booking.paid_amount || 0)) > 0 && booking.payment_status !== 'paid' && (
                      <div className="bg-orange-50 p-2 rounded border border-orange-300">
                        <div className="flex justify-between items-center">
                          <p className="text-orange-700 text-xs font-bold">
                            💰 {isPayForward ? 'Balance at Destination' : 'Balance Due at Station'}
                          </p>
                          <p className="font-bold text-orange-700">
                            {isPayForward && booking.pay_forward_currency && booking.pay_forward_currency !== booking.currency
                              ? `${booking.currency} ${(booking.pay_forward_balance || (booking.total_amount - (booking.paid_amount || 0))).toFixed(2)}`
                              : `${currencySymbol}${(booking.total_amount - (booking.paid_amount || 0)).toFixed(2)}`
                            }
                          </p>
                        </div>
                        {/* Alternate currency conversion for pay forward if applicable */}
                        {isPayForward && booking.pay_forward_currency && booking.pay_forward_currency !== booking.currency && (
                          <p className="text-orange-600 text-xs mt-0.5 text-right">
                            ({booking.pay_forward_currency === 'ZAR' ? 'R' : '$'}
                            {((booking.pay_forward_balance_alt || booking.pay_forward_balance * (booking.fx_rate || 18.5))).toFixed(2)} {booking.pay_forward_currency})
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {booking.negotiated_price_applied && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 p-2 rounded text-xs">
                      <p className="font-bold text-amber-800">Negotiated Price Applied</p>
                      <p className="text-amber-700">
                        Standard: {currencySymbol}{Number(booking.standard_price_per_seat || 0).toFixed(2)} per seat
                        {booking.negotiation_reason ? ` • ${booking.negotiation_reason}` : ''}
                      </p>
                    </div>
                  )}
                  {booking.loyalty_discount > 0 && (
                    <div className="mt-2 bg-yellow-50 p-2 rounded text-xs">
                      <p className="text-yellow-700 font-bold">🎉 Loyalty Discount: saved {currencySymbol}{booking.loyalty_discount?.toFixed(2)}</p>
                    </div>
                  )}
                  {/* Linked Luggage */}
                  {booking.luggage_refs?.length > 0 && (
                    <div className="mt-2 pt-1.5 border-t">
                      <p className="text-slate-500 text-xs mb-1 flex items-center gap-1"><Package className="h-3 w-3" /> Linked Luggage</p>
                      <div className="flex flex-wrap gap-1">
                        {booking.luggage_refs.map((ref, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => navigate(`/luggage-ticket/${encodeURIComponent(ref)}`)}
                            className="bg-purple-100 text-purple-700 text-[10px] px-2 py-1 rounded hover:bg-purple-200 font-mono"
                            title={`Open luggage ticket ${ref}`}
                          >
                            {ref}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT: Travel Requirements */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-300">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-1.5 text-sm border-b border-amber-300 pb-1.5">
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    ⚠️ Travel Requirements
                  </h4>
                  <ul className="space-y-1">
                    {TRAVEL_REQUIREMENTS.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-amber-800 text-xs">
                        <span>{req.icon}</span>
                        <span>{req.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* ── FOOTER: Issuing info + contact ── */}
              <div className="border-t pt-3 flex justify-between items-start text-xs text-slate-500">
                <div>
                  <p className="font-bold text-slate-700">PANASHE COACHES</p>
                  <p className="font-medium text-slate-600">PMB: +27843797126 | Durban: +27656139181 | Mooi River: +27622971043</p>
                  <p>Harare Office: +263 786 563 430 | +263 777 275 154</p>
                  <p className="font-semibold text-panashe-blue">www.panashecoaches.com</p>
                </div>
                <div className="text-center">
                  <p className="text-slate-500">Issued By</p>
                  <p className="font-medium text-slate-700">{booking.issued_by_name || 'System'}</p>
                  <p className="text-slate-500 mt-0.5">{booking.station_name || booking.origin || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p>Generated: {format(new Date(), 'dd MMM yyyy HH:mm')}</p>
                  <p className="font-mono font-bold">{booking.booking_ref}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Responsive Print Styles */}
        <style>{`
          @media print {
            @page { size: auto; margin: 6mm; }
            html, body { background: #ffffff !important; margin: 0 !important; padding: 0 !important; }
            nav, header, footer, .print\\:hidden, [data-testid="back-btn"], [data-testid="print-btn"] { display: none !important; }
            body * { visibility: hidden; }
            .ticket-container, .ticket-container * { visibility: visible; }
            .ticket-container {
              position: absolute;
              left: 0;
              top: 0;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
            }
            .card-panashe { box-shadow: none !important; border: 1px solid #cbd5e1 !important; }
            /* Force 2-column side-by-side grid in print out */
            .grid-cols-2 { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 0.75rem !important; }
            .flex { display: flex !important; }
            .justify-between { justify-content: space-between !important; }
            /* Logo & QR size */
            .logo { width: 75px !important; height: 75px !important; }
            .qr-box svg { width: 1.4in !important; height: 1.4in !important; }
          }
        `}</style>
      </div>
    </PublicLayout>
  );
};

export default TicketPage;
